import JSZip from 'jszip'
import { getDistance } from 'geolib'
import Papa from 'papaparse'
const { validateArchiveManifest } = require('./telemetryIngestion')

// Process Alfano ZIP file and extract GPS telemetry data
export const processAlfanoFile = async (file, { onProgress } = {}) => {
    try {
        console.log('Starting Alfano file processing...', {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type
        })

        if (onProgress) {
            onProgress({ stage: 'extracting', progress: 0 })
        }

        // Read the ZIP file
        const zip = new JSZip()
        const zipData = await zip.loadAsync(file, {
            onUpdate: (metadata) => {
                if (onProgress && metadata.percent) {
                    onProgress({ stage: 'extracting', progress: Math.round(metadata.percent) })
                }
            }
        })

        validateArchiveManifest(Object.entries(zipData.files).map(([filename, entry]) => ({
            name: entry.unsafeOriginalName || filename,
            uncompressedSize: entry._data?.uncompressedSize || 0,
            dir: entry.dir,
            encrypted: Boolean(entry._data?.encrypted),
        })))

        console.log('ZIP file loaded:', Object.keys(zipData.files))

        if (onProgress) {
            onProgress({ stage: 'parsing', progress: 0 })
        }

        // Look for CSV files in the ZIP
        // Alfano exports multiple files:
        // - Individual lap files: LAP_1_*.csv, LAP_2_*.csv, etc. (contain GPS point data)
        // - Main session file: SN*.csv (contains lap summary, no GPS points)
        // We need to collect all LAP files and combine them

        const lapFiles = []
        let sessionFile = null
        for (const [filename, zipEntry] of Object.entries(zipData.files)) {
            if (!zipEntry.dir &&
                (filename.endsWith('.csv') || filename.endsWith('.CSV')) &&
                filename.startsWith('LAP_')) {
                lapFiles.push({ filename, zipEntry })
            } else if (!zipEntry.dir && /(^|\/)SN[^/]*\.csv$/i.test(filename)) {
                sessionFile = { filename, zipEntry }
            }
        }

        if (lapFiles.length === 0) {
            throw new Error('No lap CSV files found in ZIP archive. Make sure you exported lap data from Alfano software.')
        }

        // Sort lap files by lap number (LAP_1, LAP_2, etc.)
        lapFiles.sort((a, b) => {
            const aNum = parseInt(a.filename.match(/LAP_(\d+)/)?.[1] || '0')
            const bNum = parseInt(b.filename.match(/LAP_(\d+)/)?.[1] || '0')
            return aNum - bNum
        })

        // The SN session CSV is the timing authority. LAP files contain sampled
        // channels but often have no elapsed-time column, and can include an
        // unfinished in/out lap that must not be reported as a timed lap.
        const officialLapTimes = new Map()
        let sessionMetadata = null
        if (sessionFile) {
            const sessionText = await sessionFile.zipEntry.async('text')
            const sessionRows = Papa.parse(sessionText, {
                header: false,
                dynamicTyping: true,
                skipEmptyLines: true,
            }).data
            sessionMetadata = {
                trackName: String(sessionRows[0]?.[4] || '').trim(),
                driverName: String(sessionRows[0]?.[5] || '').trim(),
                device: String(sessionRows[0]?.[10] || '').trim(),
            }
            for (const row of sessionRows.slice(2)) {
                const lapNumber = Number(row[0])
                const lapTime = Number(row[1])
                if (Number.isInteger(lapNumber) && lapNumber > 0 && Number.isFinite(lapTime) && lapTime > 0) {
                    officialLapTimes.set(lapNumber, lapTime)
                }
            }
        }

        console.log(`Found ${lapFiles.length} lap files, reading and combining...`)

        // Read and parse all lap files, keeping lap boundaries
        let allLapData = []
        let combinedColumns = null
        let lapBoundaries = [] // Track where each lap starts/ends

        for (let i = 0; i < lapFiles.length; i++) {
            const { filename, zipEntry } = lapFiles[i]
            const sourceLapNumber = parseInt(filename.match(/LAP_(\d+)/)?.[1] || String(i + 1))

            if (onProgress) {
                const progress = Math.round(((i + 1) / lapFiles.length) * 100)
                onProgress({ stage: 'parsing', progress })
            }

            const csvText = await zipEntry.async('text')

            // Parse this lap's CSV
            const parseResult = await new Promise((resolve, reject) => {
                Papa.parse(csvText, {
                    header: true,
                    dynamicTyping: true,
                    skipEmptyLines: true,
                    complete: (results) => resolve(results),
                    error: (error) => reject(error)
                })
            })

            if (parseResult.data && parseResult.data.length > 0) {
                // Store column names from first file
                if (!combinedColumns) {
                    combinedColumns = parseResult.meta.fields
                }

                // Record lap boundary
                const startIndex = allLapData.length
                const endIndex = startIndex + parseResult.data.length - 1

                lapBoundaries.push({
                    lapNumber: sourceLapNumber,
                    startIndex,
                    endIndex,
                    pointCount: parseResult.data.length
                })

                // Add lap data while preserving the point's index within its
                // own lap. Alfano lap files restart elapsed time at zero.
                allLapData = allLapData.concat(parseResult.data.map((row, lapPointIndex) => ({
                    ...row,
                    __lapNumber: sourceLapNumber,
                    __lapPointIndex: lapPointIndex,
                })))
                console.log(`Lap ${i + 1}: ${parseResult.data.length} points (indices ${startIndex}-${endIndex})`)
            }
        }

        if (allLapData.length === 0) {
            throw new Error('No data found in lap CSV files')
        }

        console.log('Combined lap data:', {
            rows: allLapData.length,
            columns: combinedColumns,
            laps: lapBoundaries.length,
            firstRow: allLapData[0]
        })

        // Create a combined parse result
        const parseResult = {
            data: allLapData,
            meta: {
                fields: combinedColumns
            }
        }

        // Try to identify the column names for GPS data
        const columns = parseResult.meta.fields
        const sampleRow = parseResult.data[0]

        // Common Alfano column names (case-insensitive)
        // Alfano 6 uses: "Lat.", "Lon."
        // Alfano 7 has both "Lat.", "Lon." (actual GPS) and "Lat. 25Hz", "Lon. 25Hz" (sample indexes)
        // We MUST prioritize the standard columns over the 25Hz columns
        const latColumns = ['lat.', 'latitude', 'lat', 'gps_lat', 'gps latitude', 'gps_latitude', 'lat. 25hz']
        const lngColumns = ['lon.', 'longitude', 'lng', 'lon', 'long', 'gps_lng', 'gps_long', 'gps longitude', 'gps_longitude', 'lon. 25hz']
        const speedColumns = ['speed', 'gps_speed', 'velocity', 'gps speed', 'gps_velocity']
        const altColumns = ['altitude', 'alt', 'elevation', 'gps_alt', 'gps altitude', 'gps_altitude']
        const timeColumns = ['time', 'timestamp', 'elapsed', 'elapsed_time', 'session_time']
        const rpmColumns = ['rpm', 'engine rpm', 'rpm 20hz', 'rpm 10hz']
        const gearColumns = ['gear', 'gear number']
        const throttleColumns = ['throttle', 'accelerator', 'gas']
        const brakeColumns = ['brake', 'brake pressure']
        const lateralAccelColumns = ['lateral acceleration', 'lateral accel', 'lat accel', 'g lateral']
        const longitudinalAccelColumns = ['longitudinal acceleration', 'longitudinal accel', 'long accel', 'g longitudinal']
        const engineTempColumns = ['engine temperature', 'water temperature', 'coolant temperature', 'temperature']

        // Find matching columns (case-insensitive, exact match preferred)
        const findColumn = (possibleNames) => {
            // First try exact match (case-insensitive)
            for (const name of possibleNames) {
                const exactMatch = columns.find(col => col.toLowerCase() === name.toLowerCase())
                if (exactMatch) return exactMatch
            }
            // Then try partial match
            return columns.find(col =>
                possibleNames.some(name => col.toLowerCase().includes(name.toLowerCase()))
            )
        }

        const latCol = findColumn(latColumns)
        const lngCol = findColumn(lngColumns)
        const speedCol = findColumn(speedColumns)
        const altCol = findColumn(altColumns)
        const timeCol = findColumn(timeColumns)
        const rpmCol = findColumn(rpmColumns)
        const gearCol = findColumn(gearColumns)
        const throttleCol = findColumn(throttleColumns)
        const brakeCol = findColumn(brakeColumns)
        const lateralAccelCol = findColumn(lateralAccelColumns)
        const longitudinalAccelCol = findColumn(longitudinalAccelColumns)
        const engineTempCol = findColumn(engineTempColumns)

        if (!latCol || !lngCol) {
            throw new Error(
                `Could not find GPS coordinates in CSV file.\n\n` +
                `Available columns: ${columns.join(', ')}\n\n` +
                `Make sure your Alfano export includes latitude and longitude columns.`
            )
        }

        console.log('Identified columns:', {
            latitude: latCol,
            longitude: lngCol,
            speed: speedCol,
            altitude: altCol,
            time: timeCol
        })

        console.log('All columns in CSV:', columns)
        console.log('First row complete data:', sampleRow)

        // Show ALL numeric columns and their sample values to help identify GPS data
        const numericColumns = {}
        columns.forEach(col => {
            const value = sampleRow[col]
            if (typeof value === 'number' && !isNaN(value)) {
                numericColumns[col] = value
            }
        })
        console.log('ALL numeric columns with sample values:', numericColumns)

        console.log('Sample data from IDENTIFIED columns:', {
            lat: sampleRow[latCol],
            lng: sampleRow[lngCol],
            speed: sampleRow[speedCol],
            alt: sampleRow[altCol],
            time: sampleRow[timeCol]
        })

        // Detect coordinate format by examining sample values
        // Alfano 6: Large integers like 38579059 (divide by 1,000,000)
        // Alfano 7: May use different format or different columns
        const sampleLat = sampleRow[latCol]
        const sampleLng = sampleRow[lngCol]

        let coordinateFormat = 'unknown'
        let divisor = 1

        // Check if already decimal degrees (-90 to 90, -180 to 180)
        if (Math.abs(sampleLat) <= 90 && Math.abs(sampleLng) <= 180) {
            coordinateFormat = 'decimal'
            divisor = 1
        }
        // Check if Alfano 6 format (large integers, typically 8 digits)
        else if (Math.abs(sampleLat) > 1000000 && Math.abs(sampleLng) > 1000000) {
            coordinateFormat = 'alfano6-integer'
            divisor = 1000000
        }
        // Check if Alfano 7 might use 10x multiplier (values like 385.79059)
        else if (Math.abs(sampleLat) > 90 && Math.abs(sampleLat) < 1000) {
            coordinateFormat = 'alfano7-10x'
            divisor = 10
        }
        // Small integers - likely WRONG COLUMNS for Alfano 7
        else if (Math.abs(sampleLat) < 100000) {
            console.warn('WARNING: The identified lat/lng columns have suspiciously small values!')
            console.warn('   This suggests these might NOT be the actual GPS coordinate columns.')
            console.warn('   Alfano 7 may store GPS data in different columns than Alfano 6.')
            console.warn('   Check the "ALL numeric columns" output above to find columns with values like:')
            console.warn('     - Large integers (e.g., 38579059 for Alfano 6 format)')
            console.warn('     - Decimal degrees (e.g., 38.579059)')
            console.warn('     - Or other formats between -90 to 90 for latitude, -180 to 180 for longitude')

            // Still try to process, but warn user
            coordinateFormat = 'possibly-incorrect-columns'
            divisor = 1
        }

        console.log('Detected coordinate format:', {
            format: coordinateFormat,
            divisor,
            sampleLat,
            sampleLng,
            afterConversion: {
                lat: sampleLat / divisor,
                lng: sampleLng / divisor
            }
        })

        // If format looks wrong, show helpful message
        if (coordinateFormat === 'possibly-incorrect-columns') {
            console.log('Looking for actual GPS coordinates in other columns...')
            console.log('   Please check the numeric columns above and look for:')
            console.log('   - Latitude: should be between -90 and 90 (or large integers like 38000000)')
            console.log('   - Longitude: should be between -180 and 180 (or large integers like -8000000)')
        }

        // Convert CSV rows to GPS points
        const allPoints = parseResult.data
            .filter(row => {
                let lat = row[latCol]
                let lng = row[lngCol]

                if (lat === null || lng === null || lat === 0 || lng === 0) {
                    return false
                }

                // Apply coordinate conversion based on detected format
                if (divisor !== 1) {
                    lat = lat / divisor
                    lng = lng / divisor
                }

                // Filter out invalid GPS points after conversion
                return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
            })
            .map((row, index) => {
                let lat = row[latCol]
                let lng = row[lngCol]

                // Apply coordinate conversion
                if (divisor !== 1) {
                    lat = lat / divisor
                    lng = lng / divisor
                }

                const speedScale = speedCol && /speed gps/i.test(speedCol) ? 0.1 : 1
                const altitudeScale = altCol && /altitude gps/i.test(altCol) ? 0.1 : 1
                const speed = speedCol ? (row[speedCol] || 0) * speedScale : 0
                const alt = altCol ? (row[altCol] || 0) * altitudeScale : 0
                const time = timeCol && row[timeCol] != null
                    ? row[timeCol]
                    : (row.__lapPointIndex ?? index) * 100

                return {
                    lat,
                    lng,
                    alt,
                    speed, // Speed may be in km/h, we'll handle conversion if needed
                    time, // Time in milliseconds or seconds
                    rpm: rpmCol ? (row[rpmCol] || 0) : null,
                    gear: gearCol ? (row[gearCol] || 0) : null,
                    throttle: throttleCol ? (row[throttleCol] || 0) : null,
                    brake: brakeCol ? (row[brakeCol] || 0) : null,
                    latAccel: lateralAccelCol ? (row[lateralAccelCol] || 0) : null,
                    longAccel: longitudinalAccelCol ? (row[longitudinalAccelCol] || 0) : null,
                    engineTemp: engineTempCol ? (row[engineTempCol] || 0) : null,
                    sourceLapNumber: row.__lapNumber,
                    index
                }
            })

        if (allPoints.length === 0) {
            throw new Error('No valid GPS points found in CSV file.')
        }

        console.log(`Processed ${allPoints.length} valid GPS points`)
        console.log('First 3 GPS points after conversion:', allPoints.slice(0, 3).map(p => ({
            lat: p.lat,
            lng: p.lng,
            speed: p.speed,
            alt: p.alt
        })))

        // Alfano exports speed in km/h. Preserve the source unit so the
        // deterministic report and UI use the same explicit unit contract.

        // Normalize time to milliseconds if it appears to be in seconds
        // (check if time values are small and incremental)
        const maxTime = Math.max(...allPoints.map(p => p.time))
        if (maxTime < 10000) {
            console.log('Converting time from seconds to milliseconds')
            allPoints.forEach(p => {
                p.time = p.time * 1000
            })
        }

        if (onProgress) {
            onProgress({ stage: 'finding-track', progress: 0 })
        }

        // Try to find the track from database
        let trackInfo = null
        try {
            const firstPoint = allPoints[0]
            const { findClosestTrack } = await import('./lineUtils.js')
            trackInfo = await findClosestTrack(firstPoint.lat, firstPoint.lng)

            if (trackInfo) {
                console.log('Found track:', trackInfo.name)
            }
        } catch (error) {
            console.log('No track found or error finding track:', error.message)
        }
        if (!trackInfo && sessionMetadata?.trackName) {
            trackInfo = {
                name: sessionMetadata.trackName,
                location: [allPoints[0].lat, allPoints[0].lng],
                source: 'alfano-session',
            }
        }

        if (onProgress) {
            onProgress({ stage: 'detecting-laps', progress: 0 })
        }

        // Use Alfano's lap boundaries instead of re-detecting
        // Each LAP_X.csv file is already one lap
        const laps = lapBoundaries.map(boundary => {
            if (officialLapTimes.size > 0 && !officialLapTimes.has(boundary.lapNumber)) {
                return null
            }
            const lapPoints = allPoints.filter(point => point.sourceLapNumber === boundary.lapNumber)

            if (lapPoints.length === 0) {
                return null
            }

            const lapTime = officialLapTimes.get(boundary.lapNumber)
                || (lapPoints[lapPoints.length - 1].time - lapPoints[0].time)

            // Calculate lap distance
            let lapDistance = 0
            for (let j = 0; j < lapPoints.length - 1; j++) {
                lapDistance += getDistance(
                    { latitude: lapPoints[j].lat, longitude: lapPoints[j].lng },
                    { latitude: lapPoints[j + 1].lat, longitude: lapPoints[j + 1].lng }
                )
            }

            return {
                lapNumber: boundary.lapNumber,
                points: lapPoints,
                lapTime: lapTime,
                formattedTime: formatLapTime(lapTime),
                distance: Math.round(lapDistance),
                avgSpeed: (lapDistance / (lapTime / 1000)).toFixed(1),
                isClean: true,
                startIndex: boundary.startIndex,
                endIndex: boundary.endIndex
            }
        }).filter(lap => lap !== null)

        console.log(`Created ${laps.length} laps from Alfano data:`, laps.map(l =>
            `Lap ${l.lapNumber}: ${l.formattedTime} (${l.distance}m, ${l.points.length} points)`
        ))

        // Get start/finish from track database or first point
        let startFinishLat = allPoints[0].lat
        let startFinishLng = allPoints[0].lng

        if (trackInfo?.combinations?.[0]?.startfinishlat && trackInfo?.combinations?.[0]?.startfinishlong) {
            startFinishLat = trackInfo.combinations[0].startfinishlat
            startFinishLng = trackInfo.combinations[0].startfinishlong
        } else if (trackInfo?.type === 'racetrack' && trackInfo?.location) {
            startFinishLat = trackInfo.location[0]
            startFinishLng = trackInfo.location[1]
        }

        return {
            allPoints,
            allLaps: laps,
            startFinishLat,
            startFinishLng,
            fileName: file.name,
            fileSize: file.size,
            trackInfo,
        }
    } catch (error) {
        console.error('Error processing Alfano file:', error)
        throw new Error(`Failed to process Alfano file: ${error.message || 'Unknown error'}`)
    }
}

// NOTE: detectLaps is not used for Alfano imports because Alfano files
// already have lap boundaries pre-defined in separate LAP_X.csv files.
// This function is kept here for potential future use with other data formats.
// eslint-disable-next-line no-unused-vars
const detectLaps = (points, track) => {
    if (points.length < 100) {
        return {
            laps: [{
                lapNumber: 1,
                points: points,
                lapTime: points[points.length - 1].time - points[0].time,
                formattedTime: formatLapTime(points[points.length - 1].time - points[0].time),
                isClean: true
            }],
            startFinishLat: points[0].lat,
            startFinishLng: points[0].lng
        }
    }

    console.log('Starting smart lap detection...', {
        totalPoints: points.length,
        hasTrack: !!track
    })

    let startFinishLat, startFinishLng

    // Step 1: Determine start/finish line location
    if (track?.combinations?.[0]?.startfinishlat && track?.combinations?.[0]?.startfinishlong) {
        startFinishLat = track.combinations[0].startfinishlat
        startFinishLng = track.combinations[0].startfinishlong
        console.log('Using start/finish from combinations:', { lat: startFinishLat, lng: startFinishLng })
    } else if (track?.type === 'racetrack' && track?.location && Array.isArray(track.location)) {
        startFinishLat = track.location[0]
        startFinishLng = track.location[1]
        console.log('Using racetrack location as start/finish:', { lat: startFinishLat, lng: startFinishLng })
    } else {
        // Auto-detect start/finish line
        const GRID_SIZE = 0.0001
        const locationCounts = new Map()

        const SAMPLE_RATE = 10
        for (let i = 0; i < points.length; i += SAMPLE_RATE) {
            const point = points[i]
            const gridKey = `${Math.floor(point.lat / GRID_SIZE)},${Math.floor(point.lng / GRID_SIZE)}`
            locationCounts.set(gridKey, (locationCounts.get(gridKey) || 0) + 1)
        }

        let maxCount = 0
        let bestGridKey = null
        for (const [key, count] of locationCounts) {
            if (count > maxCount) {
                maxCount = count
                bestGridKey = key
            }
        }

        if (!bestGridKey || maxCount < 3) {
            console.log('Could not find start/finish line, using entire recording as one lap')
            return {
                laps: [{
                    lapNumber: 1,
                    points: points,
                    lapTime: points[points.length - 1].time - points[0].time,
                    formattedTime: formatLapTime(points[points.length - 1].time - points[0].time),
                    isClean: true
                }],
                startFinishLat: points[0].lat,
                startFinishLng: points[0].lng
            }
        }

        const [gridLat, gridLng] = bestGridKey.split(',').map(Number)
        startFinishLat = (gridLat + 0.5) * GRID_SIZE
        startFinishLng = (gridLng + 0.5) * GRID_SIZE

        console.log('Auto-detected start/finish line:', {
            lat: startFinishLat,
            lng: startFinishLng,
            crossings: maxCount
        })
    }

    // Step 2: Find all crossings
    const CROSSING_THRESHOLD = 25

    // Calculate track size estimation
    let totalRecordingDistance = 0
    for (let i = 0; i < points.length - 1; i++) {
        totalRecordingDistance += getDistance(
            { latitude: points[i].lat, longitude: points[i].lng },
            { latitude: points[i + 1].lat, longitude: points[i + 1].lng }
        )
    }
    const recordingDuration = (points[points.length - 1].time - points[0].time) / 1000
    const estimatedLaps = Math.max(5, Math.min(20, Math.round(recordingDuration / 45)))
    const estimatedTrackLength = totalRecordingDistance / estimatedLaps

    console.log('Track size estimation:', {
        totalDistance: Math.round(totalRecordingDistance) + 'm',
        duration: Math.round(recordingDuration) + 's',
        estimatedTrackLength: Math.round(estimatedTrackLength) + 'm',
        estimatedLaps
    })

    const isSmallTrack = estimatedTrackLength < 2000
    const MIN_LAP_TIME = isSmallTrack ? 20000 : 40000
    const MIN_LAP_DISTANCE = isSmallTrack ? 600 : 2000
    const AWAY_THRESHOLD = isSmallTrack ? 50 : 100

    console.log('Using thresholds:', {
        trackType: isSmallTrack ? 'Small track (go-kart)' : 'Large track (racing)',
        minLapTime: (MIN_LAP_TIME / 1000) + 's',
        minLapDistance: MIN_LAP_DISTANCE + 'm',
        awayThreshold: AWAY_THRESHOLD + 'm'
    })

    const crossings = []
    let lastCrossingIndex = 0
    let lastCrossingTime = points[0].time
    let hasBeenAway = false
    let maxDistanceSinceLastCrossing = 0

    for (let i = 50; i < points.length; i++) {
        const point = points[i]
        const timeSinceLastCrossing = point.time - lastCrossingTime

        const distance = getDistance(
            { latitude: startFinishLat, longitude: startFinishLng },
            { latitude: point.lat, longitude: point.lng }
        )

        if (distance > maxDistanceSinceLastCrossing) {
            maxDistanceSinceLastCrossing = distance
        }

        if (distance > AWAY_THRESHOLD) {
            hasBeenAway = true
        }

        if (timeSinceLastCrossing < MIN_LAP_TIME) {
            continue
        }

        const isNearLine = distance < CROSSING_THRESHOLD

        if (isNearLine && hasBeenAway && maxDistanceSinceLastCrossing > AWAY_THRESHOLD) {
            let totalDistance = 0
            for (let j = lastCrossingIndex; j < i && j < points.length - 1; j++) {
                totalDistance += getDistance(
                    { latitude: points[j].lat, longitude: points[j].lng },
                    { latitude: points[j + 1].lat, longitude: points[j + 1].lng }
                )
            }

            if (totalDistance > MIN_LAP_DISTANCE) {
                console.log(`Lap crossing detected at point ${i}`)
                crossings.push(i)
                lastCrossingIndex = i
                lastCrossingTime = point.time
                hasBeenAway = false
                maxDistanceSinceLastCrossing = 0
                i += 50
            }
        }
    }

    console.log(`Found ${crossings.length} lap crossings`)

    // Step 3: Create laps from crossings
    const laps = []
    let lapStart = 0

    for (let i = 0; i < crossings.length; i++) {
        const lapEnd = crossings[i]
        const lapPoints = points.slice(lapStart, lapEnd + 1)
        const lapTime = points[lapEnd].time - points[lapStart].time

        let lapDistance = 0
        for (let j = 0; j < lapPoints.length - 1; j++) {
            lapDistance += getDistance(
                { latitude: lapPoints[j].lat, longitude: lapPoints[j].lng },
                { latitude: lapPoints[j + 1].lat, longitude: lapPoints[j + 1].lng }
            )
        }

        laps.push({
            lapNumber: i + 1,
            points: lapPoints,
            lapTime: lapTime,
            formattedTime: formatLapTime(lapTime),
            distance: Math.round(lapDistance),
            avgSpeed: (lapDistance / (lapTime / 1000)).toFixed(1),
            isClean: true,
            startIndex: lapStart,
            endIndex: lapEnd
        })

        lapStart = lapEnd
    }

    // Add final partial lap if it meets minimum requirements
    if (lapStart < points.length - 1 && (points[points.length - 1].time - points[lapStart].time) > MIN_LAP_TIME) {
        const lapPoints = points.slice(lapStart)
        const lapTime = points[points.length - 1].time - points[lapStart].time

        laps.push({
            lapNumber: laps.length + 1,
            points: lapPoints,
            lapTime: lapTime,
            formattedTime: formatLapTime(lapTime),
            isClean: true,
            isPartial: true,
            startIndex: lapStart,
            endIndex: points.length - 1
        })
    }

    if (laps.length === 0) {
        console.log('No valid laps detected - treating entire recording as one lap')
        return {
            laps: [{
                lapNumber: 1,
                points: points,
                lapTime: points[points.length - 1].time - points[0].time,
                formattedTime: formatLapTime(points[points.length - 1].time - points[0].time),
                isClean: true
            }],
            startFinishLat,
            startFinishLng
        }
    }

    console.log(`Successfully detected ${laps.length} laps`)

    return {
        laps,
        startFinishLat,
        startFinishLng
    }
}

// Format lap time from milliseconds to MM:SS.mmm
export const formatLapTime = (milliseconds) => {
    if (!milliseconds || milliseconds < 0) return '0:00.000'

    const minutes = Math.floor(milliseconds / 60000)
    const seconds = Math.floor((milliseconds % 60000) / 1000)
    const ms = Math.floor(milliseconds % 1000)

    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
}

// Validate Alfano telemetry data
export const validateAlfanoData = (points) => {
    if (!Array.isArray(points)) {
        throw new Error('Invalid data format: expected array of points')
    }

    if (points.length === 0) {
        throw new Error('No valid points found in telemetry data')
    }

    points.forEach((point, index) => {
        if (!point.lat || !point.lng) {
            throw new Error(`Invalid point at index ${index}: missing lat/lng`)
        }

        if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
            throw new Error(`Invalid point at index ${index}: lat/lng must be numbers`)
        }
    })

    return true
}
